import { API_BASE_URL } from '@synergy/config';
import { Address, NotificationOrigin, PaymentMethod } from '@synergy/models';
import { BankIcon } from '@synergy/theme';
import { Box, Tooltip, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormikContext } from 'formik';
import { useMemo, useRef, useState } from 'react';

import { Account } from '../../../../../../common/models/account';
import { AccountApi } from '../../../../../../common/api/account-api';
import { OverviewFormValues, overviewFieldNames } from '../../../models/overview-form-values';
import { PaloFormikField } from '../../../../../../common/components/palo/palo-formik-field';
import { PaloInvoiceRecipientLock } from '../../../../../../common/components/palo/palo-locked-badge';
import { PaloNotificationOverrideHint } from '../../../../../../common/components/palo/palo-notification-override-hint';
import {
    PartialAccount,
    PoolAccountAutocompleteList,
} from '../../../../../../common/components/autocomplete-list/account-autocomplete-list/pool-account-autocomplete-list';
import {
    PartialAccountContact,
    PoolAccountContactAutocompleteList,
} from '../../../../../../common/components/autocomplete-list/account-contact-autocomplete-list/pool-account-contact-autocomplete-list';
import {
    PartialAddress,
    PoolAccountAddressAutocompleteList,
} from '../../../../../../common/components/autocomplete-list/account-address-autocomplete-list/pool-account-address-autocomplete-list';
import { PaymentTermAutocompleteRef } from '../../../../../../common/components/payment-term-autocomplete/payment-term-autocomplete';
import { changeRecipientTaxRateId } from '../../../store/invoice-detail-actions';
import { fetchInvoiceAddresses, onlyOne } from '../../../../../../common/utils/only-one';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

interface PaloInvoiceHeadRecipientProps extends Pick<PaymentTermAutocompleteRef, 'getPaymentTerm'> {
    readonly?: boolean;
}

/** Whom the invoice is for: recipient, invoice address and the people there. */
export const PaloInvoiceHeadRecipient = (props: PaloInvoiceHeadRecipientProps) => {
    const intl = useIntl();
    const { readonly, getPaymentTerm } = props;
    const theme = useTheme();
    const dispatch = useThunkDispatch();
    const formik = useFormikContext<OverviewFormValues>();
    const api = useMemo(() => new AccountApi(API_BASE_URL), []);
    // The recipient may change again while its addresses are on the way.
    const lastRecipientId = useRef<number | undefined>(undefined);
    // With several to choose from the field says how many, so an empty field
    // reads as a choice to make rather than as data missing (#2250).
    const [invoiceAddressCount, setInvoiceAddressCount] = useState(0);

    const applyInvoiceAddress = async (address: Address | null) => {
        await formik.setFieldValue(overviewFieldNames.invoiceAddress, address);
        if (address?.costCentre) {
            await formik.setFieldValue(overviewFieldNames.costCentre, address.costCentre);
        }
    };

    const handleAccountSelected = async (account: Account | null) => {
        lastRecipientId.current = account?.id;

        const nextValues = {
            ...formik.values,
            recipientAccount: account,
            recipientContact: null,
            functionalAsset: null,
            invoiceAddress: null,
        };

        if (account?.paymentTermId && !formik.values.supplierAccount?.paymentTermId && getPaymentTerm) {
            nextValues.paymentTerm = getPaymentTerm(account.paymentTermId);
        }

        await formik.setValues(nextValues);
        dispatch(changeRecipientTaxRateId(account?.taxRateId ?? null));

        // Asked the way the address field asks, so a branch whose only invoice
        // address belongs to its parent gets it as well — as the order does.
        const addresses = account?.id ? await fetchInvoiceAddresses(api, account.id) : [];
        if (lastRecipientId.current !== account?.id) {
            return;
        }

        setInvoiceAddressCount(addresses.length);
        const onlyAddress = onlyOne(addresses);
        if (onlyAddress) {
            await applyInvoiceAddress(onlyAddress);
        }
    };

    const getAdornment = () => {
        const recipient = formik.values.recipientAccount;

        return [
            recipient?.paymentMethod === PaymentMethod.Debit ? (
                <Tooltip key="1" title={<FormattedMessage id="app.invoices.debit" />} placement="top-end">
                    <Box sx={{ height: '20px', mr: 1 }}>
                        <BankIcon sx={{ fontSize: 20 }} htmlColor={theme.palette.secondary.main} />
                    </Box>
                </Tooltip>
            ) : null,
            <PaloNotificationOverrideHint key="2" accountId={recipient?.id} origins={[NotificationOrigin.Invoice]} />,
        ];
    };

    return (
        <>
            <PaloFormikField<PartialAccount>
                name={overviewFieldNames.recipientAccount}
                label={<FormattedMessage id="app.label.recipient" />}
                required
                badge={<PaloInvoiceRecipientLock />}
                area="recipient"
            >
                {({ field, hasError }) => (
                    <PoolAccountAutocompleteList
                        {...field}
                        label={<></>}
                        required={false}
                        onSelected={handleAccountSelected}
                        error={hasError}
                        readOnly={readonly}
                        adornment={getAdornment()}
                    />
                )}
            </PaloFormikField>
            <PaloFormikField<PartialAddress>
                name={overviewFieldNames.invoiceAddress}
                label={<FormattedMessage id="app.label.invoiceAddress" />}
                required
                area="invoiceAddress"
            >
                {({ field, hasError }) => (
                    <PoolAccountAddressAutocompleteList
                        accountId={formik.values.recipientAccount?.id}
                        autocompleteType="InvoiceAddress"
                        // In the field, not under it: a hint there doubled the
                        // height of the row (#2262).
                        placeholder={
                            invoiceAddressCount > 1
                                ? intl.formatMessage(
                                      { id: 'app.invoices.detail.overview.invoiceAddressCount' },
                                      { count: invoiceAddressCount }
                                  )
                                : undefined
                        }
                        label={<></>}
                        disabled={!formik.values.recipientAccount}
                        onSelected={applyInvoiceAddress}
                        error={hasError}
                        readOnly={readonly}
                        required={false}
                        {...field}
                    />
                )}
            </PaloFormikField>
            <PaloFormikField<PartialAccountContact>
                name={overviewFieldNames.recipientContact}
                label={<FormattedMessage id="app.label.recipientContact" />}
                area="recipientContact"
            >
                {({ field, hasError }) => (
                    <PoolAccountContactAutocompleteList
                        accountId={formik.values.recipientAccount?.id}
                        label={<></>}
                        disabled={!formik.values.recipientAccount}
                        onSelected={value => formik.setFieldValue(field.name, value)}
                        error={hasError}
                        readOnly={readonly}
                        {...field}
                    />
                )}
            </PaloFormikField>
            <PaloFormikField<PartialAccountContact>
                name={overviewFieldNames.recipientOrderContact}
                label={<FormattedMessage id="app.label.recipientOrderContact" />}
                area="orderContact"
            >
                {({ field, hasError }) => (
                    <PoolAccountContactAutocompleteList
                        accountId={formik.values.recipientAccount?.id}
                        label={<></>}
                        disabled={!formik.values.recipientAccount}
                        onSelected={value => formik.setFieldValue(field.name, value)}
                        error={hasError}
                        readOnly={readonly}
                        {...field}
                    />
                )}
            </PaloFormikField>
        </>
    );
};
